import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { rolesApi, type RoleView } from "../../api/access";
import type { ApiError } from "../../api/client";
import { detailsMap, formatApiError } from "../../lib/apiError";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import Ic from "../../components/icons.jsx";

/**
 * `/admin/uloge` — the roles the administrator has defined (sprint 7 §5).
 * A role is just a named set of functions; editing happens on its own screen
 * (`/admin/uloge/:id`), never in a modal.
 */
export default function AdminRolesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("roles:create");
  const canEdit = hasPermission("roles:edit");
  const canDelete = hasPermission("roles:delete");

  const [items, setItems] = useState<RoleView[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Old rows stay (dimmed) while a refresh is in flight — blueprint 09 §1.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await rolesApi.list());
    } catch (err) {
      setError(formatApiError(err as ApiError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function handleDelete(row: RoleView) {
    const ok = await confirm({
      title: t("roles.confirmDelete.title", "Brisanje uloge"),
      message: t("roles.confirmDelete.message", "Obrisati ulogu „{{name}}“ ({{code}})?", { name: row.name, code: row.code }),
      confirmLabel: t("action.delete", "Obriši"),
      danger: true,
    });
    if (!ok) return;
    try {
      await rolesApi.remove(row.id);
      toast.success(t("roles.toast.deleted", "Uloga obrisana."));
      await load();
    } catch (err) {
      const e = err as ApiError;
      if (e.code === "IN_USE") {
        const count = Number(detailsMap(e)?.memberCount ?? row.memberCount);
        toast.error(t("roles.error.inUse", "Uloga ima {{count}} korisnika — prvo im dodelite drugu ulogu.", { count }));
      } else if (e.code === "SYSTEM_ROLE") {
        toast.error(t("roles.error.systemRole", "Sistemska uloga se ne može obrisati."));
      } else {
        toast.error(formatApiError(e));
      }
    }
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("roles.title", "Uloge")}</h1>
          <div className="sub">{t("roles.subtitle", "Uloga je imenovani skup funkcija koji se dodeljuje korisnicima. Pojedinačne funkcije se svakom korisniku mogu dodati ili oduzeti na njegovom ekranu „Pristup“.")}</div>
        </div>
        {canCreate && (
          <div className="page-actions">
            <button type="button" className="btn primary" onClick={() => navigate("/admin/uloge/nova")}>
              <Ic.plus /> {t("roles.new", "Nova uloga")}
            </button>
          </div>
        )}
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <section className="card" style={{ opacity: loading && items ? 0.6 : 1, transition: "opacity .15s" }} aria-busy={loading}>
        {items === null ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : items.length === 0 ? (
          <div className="empty">{t("roles.empty", "Još nema uloga.")}</div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl list-card">
              <thead>
                <tr>
                  <th>{t("roles.col.code", "Šifra")}</th>
                  <th>{t("roles.col.name", "Naziv")}</th>
                  <th>{t("roles.col.description", "Opis")}</th>
                  <th className="num">{t("roles.col.functions", "Funkcije")}</th>
                  <th className="num">{t("roles.col.members", "Članova")}</th>
                  <th style={{ textAlign: "right" }}>{t("common.actions", "Radnje")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="is-clickable" onClick={() => navigate(`/admin/uloge/${r.id}`)}>
                    <td className="mono" data-label={t("roles.col.code", "Šifra")}>{r.code}</td>
                    <td data-label={t("roles.col.name", "Naziv")}>
                      <b>{r.name}</b>
                      {r.system && <span className="tag info" style={{ marginLeft: 8 }}>{t("roles.system", "Sistemska")}</span>}
                    </td>
                    <td data-label={t("roles.col.description", "Opis")}>
                      {r.description ? r.description : <span className="muted">—</span>}
                    </td>
                    <td className="num" data-label={t("roles.col.functions", "Funkcije")}>
                      {r.allFunctions ? <span className="tag ok">{t("roles.allFunctionsShort", "Sve")}</span> : r.functionKeys.length + r.impliedKeys.length}
                    </td>
                    <td className="num" data-label={t("roles.col.members", "Članova")}>{r.memberCount}</td>
                    <td className="card-actions" style={{ textAlign: "right", whiteSpace: "nowrap" }} data-label={t("common.actions", "Radnje")} onClick={(e) => e.stopPropagation()}>
                      <button type="button" className="btn sm" onClick={() => navigate(`/admin/uloge/${r.id}`)}>
                        {canEdit ? t("action.edit", "Izmeni") : t("action.view", "Prikaži")}
                      </button>
                      {canDelete && !r.system && (
                        <button type="button" className="btn sm" style={{ ...dangerBtn, marginLeft: 6 }} onClick={() => handleDelete(r)}>
                          {t("action.delete", "Obriši")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
